import { useState } from "react";
import { Zap, Home, Droplets, Wifi, HeartHandshake, Wrench, Receipt, CalendarDays, CheckCircle2, Sparkles, FileText } from "lucide-react";
import { brl, formatDate, pct } from "@/lib/format";
import BoletoModal from "@/components/BoletoModal";
import GratitudeWall from "@/components/GratitudeWall";

function iconFor(referente) {
  const r = (referente || "").toLowerCase();
  if (r.includes("luz") || r.includes("energia")) return Zap;
  if (r.includes("aluguel") || r.includes("templo") || r.includes("imóvel")) return Home;
  if (r.includes("água")) return Droplets;
  if (r.includes("internet") || r.includes("wifi") || r.includes("tv")) return Wifi;
  if (r.includes("social") || r.includes("cesta") || r.includes("miss")) return HeartHandshake;
  if (r.includes("reforma") || r.includes("obra")) return Wrench;
  return Receipt;
}

function daysUntil(vencimento) {
  if (!vencimento) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((new Date(vencimento + "T00:00:00") - today) / 86400000);
}

export default function GoalCard({ meta, index, onHelp }) {
  const [boletoOpen, setBoletoOpen] = useState(false);
  const Icon = iconFor(meta.referente);
  const pago = meta.status === "pago";
  const days = daysUntil(meta.vencimento);
  const urgente = !pago && days !== null && days <= 7;
  const perto = !pago && meta.percentual >= 80;
  const width = pago ? 100 : meta.percentual;

  return (
    <article
      data-testid={`goal-card-${meta.id}`}
      className={`card-enter group relative flex flex-col bg-white rounded-3xl border p-5 sm:p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_12px_32px_-16px_rgba(15,42,107,0.25)] hover:shadow-[0_2px_4px_rgba(15,23,42,0.05),0_24px_48px_-20px_rgba(15,42,107,0.35)] transition-[transform,box-shadow] duration-300 hover:-translate-y-1 ${pago ? "border-emerald-200" : "border-slate-200/80"}`}
      style={{ animationDelay: `${Math.min(index, 8) * 70}ms` }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap sm:flex-nowrap">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${pago ? "bg-emerald-50 text-emerald-600" : "bg-blue-50 text-[#0f2a6b]"}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] font-bold tracking-[0.14em] uppercase text-slate-400">Referente</p>
            <h3 data-testid={`goal-title-${meta.id}`} className="font-heading text-xl sm:text-2xl font-bold text-slate-900 leading-tight line-clamp-2 break-words">
              {meta.referente}
            </h3>
          </div>
        </div>
        <span
          data-testid={`goal-status-badge-${meta.id}`}
          className={`shrink-0 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold tracking-wide border ${
            pago ? "bg-emerald-50 text-emerald-700 border-emerald-200" : urgente ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"
          }`}
        >
          {pago ? <><CheckCircle2 className="w-3.5 h-3.5" /> CONCLUÍDA</> : urgente ? "VENCE EM BREVE" : "EM ABERTO"}
        </span>
      </div>

      {meta.descricao && <p className="mt-3 text-sm text-slate-500 line-clamp-2">{meta.descricao}</p>}

      <div className="mt-5 rounded-2xl bg-slate-50/80 border border-slate-100 divide-y divide-slate-100">
        <Money label="Valor total" value={meta.valor_total} cls="text-slate-900" testid={`goal-total-${meta.id}`} />
        <Money label="Arrecadado" value={meta.valor_arrecadado} cls="text-emerald-700" testid={`goal-arrecadado-${meta.id}`} />
        <Money label="Faltam" value={meta.valor_restante} cls={pago ? "text-emerald-700" : "text-[#0f2a6b]"} testid={`goal-restante-${meta.id}`} big />
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="font-semibold text-slate-600">Progresso</span>
          <span data-testid={`goal-percent-${meta.id}`} className={`font-heading font-extrabold text-base ${pago ? "text-emerald-700" : "text-[#0f2a6b]"}`}>{pct(width)}%</span>
        </div>
        <div className="h-3.5 w-full rounded-full bg-slate-100 overflow-hidden ring-1 ring-inset ring-slate-200/70" data-testid={`goal-progress-bar-${meta.id}`}>
          <div
            className={`h-full rounded-full progress-animate progress-sheen ${pago ? "bg-gradient-to-r from-emerald-500 to-emerald-400" : "bg-gradient-to-r from-[#0f2a6b] via-blue-700 to-sky-500"}`}
            style={{ width: `${width}%` }}
          />
        </div>
        {pago ? (
          <p data-testid={`goal-concluida-${meta.id}`} className="mt-2.5 text-sm font-bold text-emerald-700 inline-flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4" /> META CONCLUÍDA
          </p>
        ) : perto ? (
          <p data-testid={`goal-perto-${meta.id}`} className="mt-2.5 text-sm font-semibold text-sky-700 inline-flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" /> Estamos perto de concluir esta meta.
          </p>
        ) : null}
      </div>

      <GratitudeWall mural={meta.mural} metaId={meta.id} />

      <div className="mt-4 flex items-center justify-between gap-2 text-sm text-slate-500">
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="w-4 h-4" />
          Vencimento <span className={`font-semibold ${urgente ? "text-rose-700" : "text-slate-700"}`}>{formatDate(meta.vencimento)}</span>
        </span>
        {meta.tem_boleto && (
          <button
            type="button"
            data-testid={`goal-boleto-thumb-${meta.id}`}
            onClick={() => setBoletoOpen(true)}
            className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-[#0f2a6b] transition-colors"
          >
            <FileText className="w-3.5 h-3.5" /> Ver guia Pix
          </button>
        )}
      </div>

      <div className="mt-5">
        {pago ? (
          <div data-testid={`goal-paid-label-${meta.id}`} className="w-full py-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 font-bold text-lg text-center inline-flex items-center justify-center gap-2">
            <CheckCircle2 className="w-5 h-5" /> META CONCLUÍDA
          </div>
        ) : (
          <button
            data-testid={`goal-help-button-${meta.id}`}
            onClick={onHelp}
            className="w-full py-4 rounded-2xl bg-[#0f2a6b] hover:bg-[#153a8f] active:scale-[0.98] text-white font-bold text-lg tracking-wide shadow-[0_10px_24px_-10px_rgba(15,42,107,0.7)] transition-[background-color,transform,box-shadow]"
          >
            AJUDAR A PAGAR
          </button>
        )}
      </div>

      {boletoOpen && <BoletoModal meta={meta} open={boletoOpen} onClose={() => setBoletoOpen(false)} />}
    </article>
  );
}

function Money({ label, value, cls, testid, big }) {
  return (
    <div className="flex items-center justify-between px-4 py-2.5 gap-3">
      <p className="text-sm font-semibold text-slate-500">{label}</p>
      <p data-testid={testid} className={`font-heading font-extrabold whitespace-nowrap ${big ? "text-xl sm:text-2xl" : "text-base sm:text-lg"} ${cls}`}>{brl(value)}</p>
    </div>
  );
}
